/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";

import { Autocomplete, Stack, TextField } from "@mui/material";
import { Nullable } from "../../utils/Interfaces";
import { DateTime } from "luxon";

export interface DateIntervalSelectorProps {
    onDatesSelected: (start: string, end: string) => void;
}

type DateInterval = {
    startDate: Nullable<DateTime>;
    endDate: Nullable<DateTime>;
};

const DateIntervalSelector = (props: DateIntervalSelectorProps) => {
    const { onDatesSelected } = props;

    const typeSelections: string[] = ["1M", "3M", "6M", "YTD", "2024", "Custom"];

    const [inputRangeType, setInputRangeType] = useState<string>(typeSelections[0]);

    const [dateInterval, setDateInterval] = useState<DateInterval>({
        startDate: null,
        endDate: null,
    });

    const darkTheme: boolean = localStorage.getItem("techwiseTheme") === "dark";

    function setStartDate(startDate: DateTime) {
        setDateInterval({
            startDate: startDate,
            endDate: dateInterval.endDate,
        });
    }

    function setEndDate(endDate: DateTime) {
        setDateInterval({
            startDate: dateInterval.startDate,
            endDate: endDate,
        });
    }

    useEffect(() => {
        if (
            dateInterval.startDate === null ||
            dateInterval.endDate === null ||
            dateInterval.startDate.toISO() === null ||
            dateInterval.endDate.toISO() === null
        ) {
            return;
        }

        onDatesSelected(
            (dateInterval.startDate.toISO() as string).replaceAll("Z", ""),
            (dateInterval.endDate.toISO() as string).replaceAll("Z", "")
        );
    }, [dateInterval]);

    useEffect(() => {
        // Strip off time data to prevent over-fetching of data.
        let endDateInit: DateTime = DateTime.now();
        let endDateRaw: DateTime = DateTime.utc(endDateInit.year, endDateInit.month, endDateInit.day, 23, 59, 59, 999);

        let startDateInit: DateTime = DateTime.now();
        let startDateRaw: DateTime = DateTime.utc(startDateInit.year, startDateInit.month, startDateInit.day, 0, 0, 0, 0);

        switch (inputRangeType) {
            case "YTD":
                startDateRaw = DateTime.utc(startDateInit.year, 1, 1, 0, 0, 0, 0);
                setDateInterval({
                    startDate: startDateRaw,
                    endDate: endDateRaw,
                });
                break;
            case "1M":
                startDateRaw = startDateRaw.minus({ months: 1 });
                setDateInterval({
                    startDate: startDateRaw,
                    endDate: endDateRaw,
                });
                break;
            case "3M":
                startDateRaw = startDateRaw.minus({ months: 3 });
                setDateInterval({
                    startDate: startDateRaw,
                    endDate: endDateRaw,
                });
                break;
            case "6M":
                startDateRaw = startDateRaw.minus({ months: 6 });
                setDateInterval({
                    startDate: startDateRaw,
                    endDate: endDateRaw,
                });
                break;
            case "2024":
                const startDt = DateTime.utc(2024, 1, 1, 0, 0, 0, 0);
                const endDt = DateTime.utc(2024, 12, 31, 23, 59, 59, 999);
                setDateInterval({
                    startDate: startDt,
                    endDate: endDt,
                });
                break;
            case "Custom":
                break;
            default:
                break;
        }
    }, [inputRangeType]);

    return (
        <Stack direction="row" justifyContent="space-between">
            <Autocomplete
                size="small"
                sx={{ mr: 1.5 }}
                fullWidth
                disablePortal
                onChange={(_event, value) => {
                    setInputRangeType(value ?? typeSelections[0]);
                }}
                getOptionKey={(option: string) => option}
                id="range_type"
                options={typeSelections}
                value={inputRangeType}
                getOptionLabel={(option: string): string => option}
                renderInput={(params) => <TextField required {...params} label="Date Range" />}
            />

            <TextField
                disabled={inputRangeType !== "Custom"}
                id="date-selector-start"
                type="date"
                value={dateInterval.startDate?.toISODate()}
                onChange={(event) => {
                    let dateArray: string[] = event.target.value.split("-");
                    let newStartDate: DateTime = DateTime.utc(
                        parseInt(dateArray[0]),
                        parseInt(dateArray[1]),
                        parseInt(dateArray[2]),
                        0,
                        0,
                        0,
                        0
                    );
                    if (newStartDate === null) {
                        return;
                    }
                    setStartDate(newStartDate);
                }}
                sx={{ colorScheme: darkTheme ? "dark" : "light", ml: 1.5, mr: 1.5 }}
                size="small"
                fullWidth
            />

            <TextField
                disabled={inputRangeType !== "Custom"}
                id="date-selector-end"
                type="date"
                value={dateInterval.endDate?.toISODate()}
                onChange={(event) => {
                    let dateArray: string[] = event.target.value.split("-");
                    let newEndDate: DateTime = DateTime.utc(
                        parseInt(dateArray[0]),
                        parseInt(dateArray[1]),
                        parseInt(dateArray[2]),
                        23,
                        59,
                        59,
                        999
                    );
                    if (newEndDate === null) {
                        return;
                    }
                    setEndDate(newEndDate);
                }}
                sx={{ colorScheme: darkTheme ? "dark" : "light", ml: 1.5 }}
                size="small"
                fullWidth
            />
        </Stack>
    );
};

export default DateIntervalSelector;
